/**
 * WizardModalShell — shared chrome for the BI wizard family.
 *
 * WQ-251 chrome-only extraction. Replaces ~140 LOC of duplicated chrome
 * across 6 wizards (AutoMLWizard, PredictionRequestForm, DataQualityLauncher,
 * FairnessLauncher, ExplainabilityLauncher, MonteCarloWizard).
 *
 * The shell owns:
 *   - Backdrop + click-outside-to-close
 *   - Modal panel sizing/styling
 *   - Header: title + step counter sub-line + close X
 *   - Progress bar (M flex segments)
 *   - Scrollable body wrapper (renders children)
 *   - Footer: Cancel / Back / Next / Submit
 *   - launchError region above the footer
 *
 * The shell does NOT own:
 *   - Step body content (children)
 *   - Validation logic (caller passes canAdvance)
 *   - Submit semantics (caller passes onSubmit + submitting + submitLabel)
 *   - Per-step state management
 *
 * Step bodies remain copy-paste-with-divergence per WQ-237 NOTES + WQ-241
 * reaffirmation. Step 2 distribution-params (MonteCarloWizard) is the most
 * divergent body in the family — concrete evidence step bodies should NOT
 * be extracted.
 */

import React from 'react';
import type { WizardModalShellProps } from './types';

const WizardModalShell: React.FC<WizardModalShellProps> = ({
  isOpen,
  onClose,
  title,
  totalSteps,
  currentStep,
  stepTitle,
  testIdPrefix,
  canAdvance,
  submitting = false,
  onBack,
  onNext,
  onSubmit,
  submitLabel = 'Launch',
  submittingLabel = 'Launching...',
  submitTestId,
  maxWidthPx = 720,
  cancelLabel = 'Cancel',
  hideBack = false,
  hideSubmit = false,
  launchError,
  children,
}) => {
  if (!isOpen) return null;

  const isFirstStep = currentStep <= 1;
  const isLastStep = currentStep >= totalSteps;
  const submitTestIdResolved = submitTestId ?? `${testIdPrefix}-launch`;
  const showBack = !isFirstStep && !hideBack;
  const showSubmit = isLastStep && !hideSubmit;

  return (
    <div
      data-testid={testIdPrefix}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary)',
          borderRadius: 12,
          border: '1px solid var(--border)',
          width: `min(${maxWidthPx}px, 92vw)`,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
              <div
                style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}
                data-testid={`${testIdPrefix}-step-title`}
              >
                Step {currentStep} of {totalSteps}: {stepTitle}
              </div>
            </div>
            <button
              data-testid={`${testIdPrefix}-close`}
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                fontSize: 20,
              }}
              aria-label="Close"
            >
              {'\u00d7'}
            </button>
          </div>
          <div
            style={{ display: 'flex', gap: 4, marginTop: 12 }}
            data-testid={`${testIdPrefix}-progress`}
          >
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => (
              <div
                key={n}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  background: n <= currentStep ? 'var(--accent-primary)' : 'var(--border)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {children}
          {launchError && (
            <div
              data-testid={`${testIdPrefix}-launch-error`}
              style={{
                padding: 12,
                background: '#fef2f2',
                color: '#dc2626',
                borderRadius: 8,
                marginTop: 12,
              }}
            >
              {launchError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            data-testid={`${testIdPrefix}-cancel`}
            onClick={onClose}
            style={{
              padding: '6px 16px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {showBack && (
              <button
                data-testid={`${testIdPrefix}-back`}
                onClick={onBack}
                style={{
                  padding: '6px 16px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
            )}
            {!isLastStep && (
              <button
                data-testid={`${testIdPrefix}-next`}
                onClick={onNext}
                disabled={!canAdvance}
                style={{
                  padding: '6px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'var(--accent-primary)',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: canAdvance ? 'pointer' : 'not-allowed',
                  opacity: canAdvance ? 1 : 0.5,
                }}
              >
                Next
              </button>
            )}
            {showSubmit && (
              <button
                data-testid={submitTestIdResolved}
                onClick={onSubmit}
                disabled={submitting}
                style={{
                  padding: '6px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#22c55e',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.5 : 1,
                }}
              >
                {submitting ? submittingLabel : submitLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardModalShell;
